import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useTranslation } from '@/utils/locales/messages'
import { useLanguageStore } from '@/shared/stores/languageStore'
import { useSessionStore } from '@/shared/stores/sessionStore'
import { isLocal, isDevelopment } from '@/shared/config/env'
import {
  checkMenuViewPermission,
  resolveDefaultLandingPathFromAuth,
  resolveEntityListPath,
} from '@/shared/utils/permissionUtils'
import { isValidUserAppAuth } from '@/shared/utils/authSessionUtils'


/**
 * 자동으로 features 폴더에서 라우트를 가져오는 함수
 */
const loadFeatureRoutes = (): RouteRecordRaw[] => {
  const routes: RouteRecordRaw[] = []

  // features 폴더에서 routes.ts 파일을 자동으로 찾아서 import
  const featureModules = import.meta.glob('../../features/*/routes.ts', { eager: true })

  console.log('Loading feature routes:', Object.keys(featureModules))

  Object.entries(featureModules).forEach(([path, module]: [string, any]) => {
    // // auth routes는 수동으로 등록하므로 제외
    // if (path.includes('/auth/')) {
    //   console.log('Skipping auth routes (manually registered):', path)
    //   return
    // }

    console.log('Processing module:', path, module)

    // default export 우선 확인
    let routeArray: RouteRecordRaw[] = []
    if (module.default && Array.isArray(module.default)) {
      routeArray = module.default
    } else {
      // named export 확인 (authRoutes, dashboardRoutes 등)
      Object.keys(module).forEach(key => {
        if (key.endsWith('Routes') && Array.isArray(module[key])) {
          routeArray = module[key]
        }
      })
    }

    // devOnly 메타가 있는 라우트는 local/dev 환경에서만 추가
    const filteredRoutes = routeArray.filter((route: RouteRecordRaw) => {
      if (route.meta?.devOnly === true) {
        const shouldInclude = isLocal() || isDevelopment()
        if (!shouldInclude) {
          console.log(`Skipping devOnly route: ${route.path} (not in local/dev environment)`)
        }
        return shouldInclude
      }
      return true
    })

    console.log('Loaded routes:', filteredRoutes.map((r: any) => r.name))
    routes.push(...filteredRoutes)
  })

  return routes
}

/**
 * All Routes - 루트 경로는 대시보드 또는 로그인으로 리다이렉트
 */
const routes: RouteRecordRaw[] = [
  // 딥링크 핸들러 (최우선 매칭)
  {
    path: '/d/:linkCode',
    name: 'DeepLinkHandler',
    component: () => import('@/views/DeepLinkHandler.vue'),
    meta: {
      title: 'Deep Link',
      layout: 'blank',
      requiresAuth: false
    }
  },
  // 외부 링크 중간 페이지
  {
    path: '/external-redirect',
    name: 'ExternalRedirect',
    component: () => import('@/views/ExternalLinkRedirect.vue'),
    meta: {
      titleKey: 'common.pageTitle.externalRedirect',
      layout: 'blank',
      requiresAuth: false
    }
  },
  {
    path: '/entity-no-permission',
    name: 'EntityNoPermission',
    component: () => import('@/views/EntityNoPermissionPage.vue'),
    meta: {
      title: 'Dashboard',
      layout: 'default',
      requiresAuth: true
    }
  },
   // 자동으로 로드된 feature 라우트들 (루트 경로보다 먼저)
   ...loadFeatureRoutes(),

  // 루트 경로는 인증 상태에 따라 리다이렉트 (beforeEach에서 처리)
  {
    path: '/',
    name: 'Root',
    component: () => import('@/shared/layouts/blank/BlankLayout.vue'),
    meta: {
      requiresAuth: false,
      layout: 'blank',
      isRootRedirect: true // 루트 경로임을 표시
    }
  },
  // 404 라우트 (마지막에 배치)
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundPage.vue'),
    meta: {
      title: 'common.pageTitle.notFound',
      layout: 'blank',
      requiresAuth: false
    },
  },
]

// 등록된 라우트 확인
console.log('Registered routes:', routes.map(r => ({ path: r.path, name: r.name })))

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// 라우터 생성 후 등록된 라우트 확인
console.log('Router created with routes:', router.getRoutes().map(r => ({ path: r.path, name: r.name })))

/**
 * Navigation Guards - 인증 체크
 */
// 리다이렉트 루프 안전장치: 경로 불일치 등으로 가드가 짧은 시간에 과도하게 재진입하면
// 매 진입마다 /api/auth/me 를 새로 호출해 서버에 부담이 간다(무한 호출). 임계 초과 시
// 강제로 현재 목적지로 통과시켜 루프를 끊는다. (3초 창에서 20회 초과 = 튕김 루프로 간주)
let _guardBurst = 0;
let _guardBurstTs = 0;
router.beforeEach(async (to, from, next) => {
  const _now = Date.now();
  if (_now - _guardBurstTs > 3000) { _guardBurst = 0; _guardBurstTs = _now; }
  if (++_guardBurst > 20) {
    console.error('[Router Guard] redirect loop detected — breaking to avoid /api/auth/me flood', { to: to.fullPath, from: from.fullPath });
    _guardBurst = 0;
    return next();
  }
  console.log('Router guard - navigating to:', to.path, 'from:', from.path);
  console.log('Matched routes:', to.matched.map(r => ({ path: r.path, name: r.name, meta: r.meta })));

  // 같은 경로로의 네비게이션 (쿼리만 다른 경우) - 인증 체크 스킵
  // _refresh 파라미터로 인한 재네비게이션 등에서 중복 API 호출 방지
  // 단, 초기 로드 시 (from.name이 undefined)에는 스킵하지 않음
  if (to.path === from.path && from.name !== undefined) {
    console.log('Same route navigation detected, skipping auth check');
    return next();
  }

  // 딥링크 접근 제한 경로 체크 (/setup-entity/application 등)
  // URL에 토큰을 노출하지 않고 세션만 확인
  // 단, DeepLinkHandler에서 라우팅하는 경우는 스킵 (이미 딥링크 검증 완료)
  const deepLinkProtectedPaths = ['/setup-entity/application'];
  const isDeepLinkProtected = deepLinkProtectedPaths.some(path => to.path === path || to.path.startsWith(path + '/'));
  const isFromDeepLinkHandler = from.name === 'DeepLinkHandler' || from.matched.some(r => r.name === 'DeepLinkHandler');

  if (isDeepLinkProtected && !isFromDeepLinkHandler) {
    // 로그인 사용자는 일반 접근 허용 (목록에서 row 클릭 시 상세 진입)
    const sessionStore = useSessionStore();
    const hasLoggedInSession = !!sessionStore.user;
    if (hasLoggedInSession) {
      console.log('로그인 세션 존재 - 딥링크 보호 경로 일반 접근 허용', { path: to.path });
    } else {
    // DeepLinkHandler에서 라우팅하는 경우가 아닐 때만 접근 권한 확인
    // 세션에서 접근 권한 확인 (URL에 토큰 노출 없음)
    try {
      const currentLanguage = localStorage.getItem('preferred-language') || 'en';
      const response = await fetch('/api/deep-link/verify-access', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Language': currentLanguage,
          'Accept-Language': currentLanguage
        },
        body: JSON.stringify({ targetUrl: to.path })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('딥링크 접근 권한 없음 - 접근 차단', { path: to.path, error: errorData });
        // 접근 차단 - 홈으로 리다이렉트
        return next('/');
      }

      const result = await response.json();
      if (!result.success || !result.data?.valid) {
        console.log('딥링크 접근 권한이 유효하지 않음 - 접근 차단', { path: to.path });
        return next('/');
      }

      console.log('딥링크 접근 권한 확인됨', { path: to.path });
    } catch (error: any) {
      console.error('딥링크 접근 권한 확인 중 오류 발생 - 접근 차단', { path: to.path, error: error.message });
      return next('/');
    }
    }
  } else if (isDeepLinkProtected && isFromDeepLinkHandler) {
    // DeepLinkHandler에서 라우팅하는 경우는 접근 권한 체크 스킵
    console.log('DeepLinkHandler에서 라우팅 중 - 딥링크 접근 권한 체크 스킵', { path: to.path });
    return next();
  }

  // 인증 데이터를 저장할 변수 (여러 곳에서 사용)
  let authDataFromCheck: any = null;

  // public 페이지 목록 (인증 불필요)
  const publicPages = ['/auth/login', '/auth/register', '/','signUp', '/tsign/signing', '/tsign/completed', '/setup-entity/application'];
  const isPublicPage = publicPages.includes(to.path)
    || to.path.startsWith('/tsign/completed')
    || to.path.startsWith('/setup-entity/application')
    || to.path.startsWith('/custom-form/fill');

  // 인증이 필요한 페이지인지 확인 (명시적으로 requiresAuth가 true인 경우만)
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth === true)

  console.log('Auth check - requiresAuth:', requiresAuth, 'isPublicPage:', isPublicPage, 'path:', to.path);

  // 인증이 필요한 페이지인 경우에만 체크
  if (requiresAuth && !isPublicPage) {
    // 세션 기반 인증 체크 (X-Language 헤더 포함)
    let isAuthenticated = false;
    let isBackendConnected = false;

    try {
      // localStorage에서 언어 가져오기 (Pinia 초기화 전에도 사용 가능)
      const currentLanguage = localStorage.getItem('preferred-language') || 'en';

      // 타임아웃 설정 (5초) - 백엔드 연결 실패 시 빠르게 감지
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // 캐시 방지를 위한 타임스탬프 추가
      const response = await fetch(`/api/auth/me?_t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'X-Language': currentLanguage,
          'Accept-Language': currentLanguage,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      isBackendConnected = true;
      
      // 응답 본문을 확인하여 실제 인증 상태 체크
      let authData: any = null;
      if (response.ok) {
        try {
          authData = await response.json();
          // success가 true이고 user가 있는 경우만 인증됨으로 판단
          // 어드민 세션은 사용자 앱에서 미인증 처리 (쿠키 공유 리다이렉트 루프 방지)
          isAuthenticated = isValidUserAppAuth(authData);
        } catch (parseError) {
          // JSON 파싱 실패 시 response.ok만으로 판단
          isAuthenticated = response.ok;
        }
      } else {
        isAuthenticated = false;
      }
      
      console.log('Auth check result:', { isAuthenticated, status: response.status });

      // 인증 체크 시간 마킹 및 세션 스토어 업데이트
      if (isAuthenticated && authData) {
        try {
          const sessionStore = useSessionStore();
          sessionStore.markAuthCheckTime();
          // 세션 스토어에 사용자 정보 업데이트 (jurisdiction 확인을 위해)
          if (authData.user) {
            sessionStore.setUser(authData.user);
            // permissions가 user 객체에 포함된 경우 함께 저장
            if (authData.user.permissions) {
              sessionStore.setPermissions(authData.user.permissions);
            }
          }
        } catch (e) {
          // Pinia 초기화 전일 수 있으므로 무시
        }
      }

      // 약관·개인정보 동의 게이트: /me 의 consent 를 전역 상태로 반영(모달은 FullLayout에서 표시).
      if (isAuthenticated && authData?.consent) {
        try {
          const { setConsent } = await import('@/shared/composables/useTermsConsent');
          setConsent(authData.consent);
        } catch (e) { /* ignore */ }
      }

      // 하드 차단(2026-07): 결제 필요(유료 만료/체험 만료) 시 결제화면 외 접근 차단 + 리다이렉트.
      if (isAuthenticated && authData?.access) {
        try {
          const { setAccess } = await import('@/shared/composables/useSubscriptionStatus');
          setAccess(authData.access);
        } catch (e) { /* ignore */ }
        // 결제(billing) 화면은 /settings/billing 으로 이관됨. 구 /pages/company-settings 는
        // /settings/billing 으로 redirect 되므로, 둘 다 허용하지 않으면
        // 가드가 blocked 사용자를 구경로→(redirect)신경로→가드차단→구경로… 로 무한 루프시킨다(=me 폭주).
        // (2026-09) /settings/entities 도 허용 — 만료 상태에서 결제 전에 엔티티를 켜고 꺼서 결제 대상 수를
        // 조절할 수 있어야 한다(백엔드 subscriptionMiddleware 도 entities 토글을 예외 허용).
        const blockedAllowed = to.path?.startsWith('/settings/billing')
          || to.path?.startsWith('/settings/entities')
          || to.path?.startsWith('/pages/company-settings')
          || to.path?.startsWith('/auth')
          || to.path === '/logout';
        if (authData.access.blocked && !blockedAllowed) {
          return next({ path: '/settings/billing', query: { blocked: '1' } });
        }
      }
    } catch (error: any) {
      // 네트워크 에러 또는 타임아웃 감지
      const isNetworkError = error.name === 'AbortError' || 
                            error.name === 'TypeError' || 
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('NetworkError');
      
      console.log('Auth check failed:', {
        error: error.message,
        isNetworkError,
        name: error.name
      });
      
      // 백엔드 연결 실패 시 세션 스토어 초기화 (가능한 경우)
      if (isNetworkError) {
        try {
          const { useSessionStore } = await import('@/shared/stores/sessionStore');
          const sessionStore = useSessionStore();
          sessionStore.clearSession();
          sessionStore.isInitialized = false;
          console.log('Backend connection failed - session cleared');
        } catch (storeError) {
          // 세션 스토어 초기화 실패는 무시 (Pinia 초기화 전일 수 있음)
          console.warn('Failed to clear session store:', storeError);
        }
      }
      
      isAuthenticated = false;
      isBackendConnected = false;
    }

    // 백엔드 연결 실패 또는 인증 실패 시 로그인 페이지로 리다이렉트
    if (!isBackendConnected || !isAuthenticated) {
      console.log('Backend not connected or not authenticated, redirecting to login page');
      return next({ path: '/auth/login', query: { redirect: to.fullPath } });
    }
  }

  // Entity 경로 접근 시 jurisdiction 확인하여 리다이렉트
  // 인증이 필요한 페이지가 아니어도 Entity 경로는 체크
  if (to.path === '/entity' || to.path === '/Entity' || to.path === '/entity_my' || to.path === '/Entity_my' || 
      to.path.startsWith('/entity/') || to.path.startsWith('/Entity/') || 
      to.path.startsWith('/entity_my/') || to.path.startsWith('/Entity_my/')) {
    
    // 인증 체크 (Entity 경로는 인증 필요)
    let isAuthenticated = false;
    let authData: any = null;
    
    try {
      const currentLanguage = localStorage.getItem('preferred-language') || 'en';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`/api/auth/me?_t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'X-Language': currentLanguage,
          'Accept-Language': currentLanguage,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        try {
          authData = await response.json();
          isAuthenticated = isValidUserAppAuth(authData);
        } catch (parseError) {
          isAuthenticated = response.ok;
        }
      }
      
      if (!isAuthenticated) {
        return next('/auth/login');
      }
      
      // 세션 스토어 업데이트 및 jurisdiction 확인
      try {
        const sessionStore = useSessionStore();
        if (authData && authData.user) {
          sessionStore.setUser(authData.user);
          // permissions 저장
          if (authData.user.permissions) {
            sessionStore.setPermissions(authData.user.permissions);
          }
        }
        
        // jurisdiction 확인
        const userData = authData?.user || authData?.data?.user;
        const jurisdiction = userData?.customer?.jurisdiction || sessionStore.getCustomerInfo()?.jurisdiction;
        
        console.log('Entity path jurisdiction check:', { 
          path: to.path, 
          jurisdiction, 
          hasUserData: !!userData,
          hasCustomer: !!userData?.customer
        });

        const permissions = sessionStore.getPermissions;
        const entityPath = resolveEntityListPath(jurisdiction);
        if (permissions?.isAuthEnabled && !checkMenuViewPermission(permissions, entityPath)) {
          const landingPath = resolveDefaultLandingPathFromAuth(authData, jurisdiction);
          if (to.path !== landingPath) {
            return next({ path: landingPath, replace: true });
          }
        }
        
        // jurisdiction에 따라 올바른 경로로 리다이렉트
        if (jurisdiction === 'MY') {
          // MY인데 /entity로 접근한 경우
          if (to.path.startsWith('/entity/') || to.path.startsWith('/Entity/') || to.path === '/entity' || to.path === '/Entity') {
            const redirectPath = to.path.replace(/^\/Entity(\/|$)/, '/Entity_my$1').replace(/^\/entity(\/|$)/, '/entity_my$1');
            console.log('Redirecting to entity_my:', redirectPath);
            return next({ path: redirectPath, query: to.query, hash: to.hash, replace: true });
          }
        } else {
          // MY가 아닌데 /entity_my로 접근한 경우
          if (to.path.startsWith('/entity_my/') || to.path.startsWith('/Entity_my/') || to.path === '/entity_my' || to.path === '/Entity_my') {
            const redirectPath = to.path.replace(/^\/Entity_my(\/|$)/, '/Entity$1').replace(/^\/entity_my(\/|$)/, '/entity$1');
            console.log('Redirecting to entity:', redirectPath);
            return next({ path: redirectPath, query: to.query, hash: to.hash, replace: true });
          }
        }
      } catch (e) {
        console.warn('Failed to check jurisdiction in router guard:', e);
      }
    } catch (error: any) {
      console.warn('Failed to check auth for entity path:', error);
      return next('/auth/login');
    }
  }

  // Dashboard 경로: view 권한 없으면 Dashboard → Entity → 기본 페이지 우선순위로 리다이렉트
  if (to.path === '/dashboard' || to.path === '/Dashboard') {
    let isAuthenticated = false;
    let authData: any = null;

    try {
      const currentLanguage = localStorage.getItem('preferred-language') || 'en';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`/api/auth/me?_t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'X-Language': currentLanguage,
          'Accept-Language': currentLanguage,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        try {
          authData = await response.json();
          isAuthenticated = isValidUserAppAuth(authData);
        } catch {
          isAuthenticated = response.ok;
        }
      }

      if (!isAuthenticated) {
        return next('/auth/login');
      }

      const sessionStore = useSessionStore();
      if (authData?.user) {
        sessionStore.setUser(authData.user);
        if (authData.user.permissions) {
          sessionStore.setPermissions(authData.user.permissions);
        }
      }

      const jurisdiction =
        authData?.user?.customer?.jurisdiction
        || sessionStore.getCustomerInfo()?.jurisdiction;
      const permissions = sessionStore.getPermissions;

      if (permissions?.isAuthEnabled && !checkMenuViewPermission(permissions, '/dashboard')) {
        const landingPath = resolveDefaultLandingPathFromAuth(authData, jurisdiction);
        if (to.path !== landingPath) {
          return next({ path: landingPath, replace: true });
        }
      }
    } catch (error: any) {
      console.warn('Failed to check auth for dashboard path:', error);
      return next('/auth/login');
    }
  }

  // Register 경로 접근 시 jurisdiction 확인하여 리다이렉트
  // 요구사항: MY 세션이면 /register 직접 입력해도 /register/my 로 강제 이동
  // (SG 등 비-MY 는 기존 /register 목록 유지 — 운영용)
  if (to.path === '/register' || to.path === '/Register') {
    try {
      const sessionStore = useSessionStore();
      const customerInfo = sessionStore.getCustomerInfo();
      const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
      if (jurisdiction === 'MY' || jurisdiction === 'MALAYSIA') {
        return next({ path: '/register/my', query: to.query, hash: to.hash, replace: true });
      }
    } catch (e) {
      console.warn('Failed to check jurisdiction for register redirect:', e);
    }
  }

  // 비-MY 로그인 세션이 /register/my URL로 직접 접근한 경우 일반 Register 목록으로 이동
  if (to.path === '/register/my' || to.path === '/Register/my') {
    try {
      const sessionStore = useSessionStore();
      const customerInfo = sessionStore.getCustomerInfo();
      const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
      const isMy = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
      if (!isMy) {
        return next({ path: '/register', query: to.query, hash: to.hash, replace: true });
      }
    } catch (e) {
      console.warn('Failed to check jurisdiction for /register/my:', e);
    }
  }

  // /register/current 는 jurisdiction 별로 다른 화면을 쓰며 URL 은 유지 (사이드바 active 분리)
  // MY → My Register Book, SG → Current Register (현행 테이블)

  // 루트 경로(/)에서 인증 상태 확인 후 적절한 페이지로 리다이렉트
  if (to.path === '/' || to.meta.isRootRedirect) {
    console.log('Root path detected, checking auth...');
    let isBackendConnected = false;

    try {
      // localStorage에서 언어 가져오기 (Pinia 초기화 전에도 사용 가능)
      const currentLanguage = localStorage.getItem('preferred-language') || 'en';

      // 타임아웃 설정 (2초) - 루트 경로 리다이렉트는 빠르게 처리
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      // 캐시 방지를 위한 타임스탬프 추가
      const response = await fetch(`/api/auth/me?_t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'X-Language': currentLanguage,
          'Accept-Language': currentLanguage,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      isBackendConnected = true;
      
      // 응답 본문을 확인하여 실제 인증 상태 체크
      let isAuthenticated = false;
      let authData: any = null;
      if (response.ok) {
        try {
          authData = await response.json();
          // success가 true이고 user가 있는 경우만 인증됨으로 판단
          // 어드민 세션은 사용자 앱에서 미인증 처리
          isAuthenticated = isValidUserAppAuth(authData);
        } catch (parseError) {
          // JSON 파싱 실패 시 response.ok만으로 판단
          isAuthenticated = response.ok;
        }
      }
      
      if (isAuthenticated) {
        console.log('User authenticated, redirecting to client');
        // 인증 체크 시간 마킹 및 세션 스토어 업데이트
        let redirectPath = '/entity';
        try {
          const sessionStore = useSessionStore();
          sessionStore.markAuthCheckTime();

          let jurisdiction: string | null = null;
          if (authData && authData.user) {
            sessionStore.setUser(authData.user);
            if (authData.user.permissions) {
              sessionStore.setPermissions(authData.user.permissions);
            }
            jurisdiction = authData.user?.customer?.jurisdiction || null;
          }

          if (!jurisdiction) {
            try {
              jurisdiction = sessionStore.getCustomerInfo()?.jurisdiction || null;
            } catch (e) {
              // 세션 스토어 접근 실패는 무시
            }
          }

          redirectPath = resolveDefaultLandingPathFromAuth(authData, jurisdiction);

          console.log('Root path landing check:', { jurisdiction, redirectPath });
        } catch (e) {
          console.warn('Failed to determine landing path, using default path:', e);
        }
        // replace를 사용하여 히스토리에 추가하지 않음
        return next({ path: redirectPath, replace: true });
      } else {
        console.log('User not authenticated, redirecting to login');
        return next({ path: '/auth/login', replace: true });
      }
    } catch (error: any) {
      // 네트워크 에러 또는 타임아웃 감지
      const isNetworkError = error.name === 'AbortError' || 
                            error.name === 'TypeError' || 
                            error.message?.includes('Failed to fetch') ||
                            error.message?.includes('NetworkError');
      
      console.log('Auth check failed:', {
        error: error.message,
        isNetworkError,
        name: error.name
      });
      
      // 백엔드 연결 실패 시 세션 스토어 초기화 (가능한 경우)
      if (isNetworkError) {
        try {
          const { useSessionStore } = await import('@/shared/stores/sessionStore');
          const sessionStore = useSessionStore();
          sessionStore.clearSession();
          sessionStore.isInitialized = false;
          console.log('Backend connection failed - session cleared');
        } catch (storeError) {
          // 세션 스토어 초기화 실패는 무시 (Pinia 초기화 전일 수 있음)
          console.warn('Failed to clear session store:', storeError);
        }
      }
      
      console.log('Backend not connected, redirecting to login');
      return next({ path: '/auth/login', replace: true });
    }
  }

  console.log('Proceeding to:', to.path);
  next();
})

// 페이지 타이틀 설정 - afterEach에서 설정해야 리다이렉트 후 최종 페이지의 타이틀이 적용됨
// www.lettaga.sg 타이틀 규칙 적용: {페이지명}::Lettaga
// 다국어 지원: title을 i18n 키로 사용하여 번역 시도, 번역 실패 시 그대로 사용
router.afterEach((to, from) => {
  // _refresh 파라미터가 있으면 URL에서 제거 (컴포넌트 재마운트 후 정리)
  if (to.query._refresh && to.path === from.path) {
    // 같은 경로인 경우에만 _refresh 제거 (히스토리에 추가하지 않음)
    // history.replaceState를 사용하여 라우터 가드 재실행 방지
    setTimeout(() => {
      const otherParams = Object.entries(to.query)
        .filter(([k]) => k !== '_refresh')
        .map(([k, v]) => [k, Array.isArray(v) ? v[0] || '' : v || ''] as [string, string]);
      const newUrl = to.path + (otherParams.length > 0
        ? '?' + new URLSearchParams(otherParams).toString()
        : '');
      window.history.replaceState(history.state, '', newUrl);
    }, 100);
  }
  
  const title = to.meta.title as string
  const titleKey = to.meta.titleKey as string
  const isLoginPage = to.path === '/auth/login'

  try {
    // 번역 함수 초기화
    const { createSyncT, getCurrentLanguage } = useTranslation()
    const languageStore = useLanguageStore()
    
    // createSyncT는 반응형 객체를 기대하므로 languageStore.currentLanguage를 직접 전달
    // languageStore.currentLanguage는 computed ref이므로 { value: ... } 형태로 전달
    // 언어 스토어가 초기화되지 않았을 수 있으므로 fallback으로 getCurrentLanguage() 사용
    const currentLangRef = languageStore.currentLanguage || { value: getCurrentLanguage() }
    const t = createSyncT(currentLangRef)
    if (isLoginPage) {
      // 로그인 페이지는 슬로건 포함
      const loginTitleKey = 'common.pageTitle.login'
      const translatedTitle = t(loginTitleKey)
      // 번역 성공 시 (키가 그대로 반환되지 않은 경우) 번역된 값 사용, 실패 시 기본값 'Login' 사용
      const loginTitle = translatedTitle !== loginTitleKey ? translatedTitle : 'Login'
      document.title = `${loginTitle}::Lettaga - World #1 e-Filing Solution`
    } else if (title || titleKey) {
      let finalTitle = title
      if (titleKey) {
        // titleKey가 있으면 titleKey만 시도 (title은 fallback으로만 사용)
        const translatedTitleKey = t(titleKey)
        if (translatedTitleKey !== titleKey) {
          finalTitle = translatedTitleKey
        }
      } else if (title) {
        // titleKey가 없고 title만 있는 경우 title을 번역 키로 시도
        const translatedTitle = t(title)
        if (translatedTitle !== title) {
          finalTitle = translatedTitle
        }
      }
      document.title = `${finalTitle}::Lettaga`
    } else {
      document.title = 'Lettaga'
    }
  } catch (e) {
    // Pinia 초기화 전 또는 번역 실패 시 기본값 사용
    if (isLoginPage) {
      document.title = 'Login::Lettaga - World #1 e-Filing Solution'
    } else if (title) {
      document.title = `${title}::Lettaga`
    } else {
      document.title = 'Lettaga'
    }
  }
})

export default router
